import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { getSupabaseClient, clearSupabaseClientCache } from './supabase-client';
import { normalizeCategoryToSlug } from '../app/constants/marketCategories';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(s: string | null | undefined): boolean {
  return typeof s === 'string' && UUID_RE.test(s);
}

const SUPABASE_FUNCTION_ID = import.meta.env.VITE_SUPABASE_FUNCTION_ID || 'make-server-88445585';
const API_BASE = `https://${projectId}.supabase.co/functions/v1/${SUPABASE_FUNCTION_ID}`;
const BUCKET_PREFIX = 'make-88445585-';
const KNOWN_BUCKET_ALIASES = new Set(['avatars', 'events', 'listings', 'chat-media']);
const USER_PROFILE_CACHE_TTL_MS = 60_000;
const userProfileCache = new Map<string, { value: any; expiresAt: number }>();
const userProfileInFlight = new Map<string, Promise<any>>();

function getAuthToken(): string | null {
  return localStorage.getItem('access_token');
}

function isHttpUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

function normalizeBucketName(bucket: string): string {
  if (!bucket) return bucket;
  return bucket.startsWith(BUCKET_PREFIX) ? bucket : `${BUCKET_PREFIX}${bucket}`;
}

const SUPABASE_URL = `https://${projectId}.supabase.co`;

function resolveRenderableImage(
  rawValue: string,
  defaultBucket: string,
  _accessToken?: string | null
): string {
  if (!rawValue) return '';
  if (isHttpUrl(rawValue)) {
    // Convert signed URLs to public URLs so they don't expire
    const signedMatch = rawValue.match(/\/storage\/v1\/object\/sign\/([^?]+)/);
    if (signedMatch) {
      return `${SUPABASE_URL}/storage/v1/object/public/${signedMatch[1]}`;
    }
    return rawValue.trim();
  }
  const bucket = normalizeBucketName(defaultBucket);
  const path = rawValue.replace(/^\/+/, '');
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

async function resolveProfileImageFields(profile: any, accessToken?: string | null) {
  if (!profile) return profile;
  const normalizedProfile = normalizeProfileData(profile);
  const rawAvatar = normalizedProfile.avatar_url || normalizedProfile.avatar || '';
  if (!rawAvatar) return normalizedProfile;

  const avatarUrl = await resolveRenderableImage(String(rawAvatar), 'avatars', accessToken);
  return normalizeProfileData({
    ...normalizedProfile,
    avatar_url: avatarUrl,
    avatar: avatarUrl,
  });
}

function normalizeInterests(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry: any) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry: string) => entry.length > 0);
}

function normalizeProfileChildren(value: any): Array<{ name: string | null; gender: string | null; birth_date: string | null; age: string | null; age_range: string | null }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((child: any) => {
      const nameRaw = typeof child?.name === 'string' ? child.name.trim() : '';
      const genderRaw = typeof child?.gender === 'string' ? child.gender.trim() : '';
      const birthDateRaw = typeof child?.birth_date === 'string' ? child.birth_date.trim() : '';
      const ageRaw = typeof child?.age === 'string'
        ? child.age.trim()
        : (typeof child?.age_range === 'string' ? child.age_range.trim() : '');

      return {
        name: nameRaw || null,
        gender: genderRaw || null,
        birth_date: birthDateRaw || null,
        age: ageRaw || null,
        age_range: ageRaw || null,
      };
    })
    .filter((child: any) => child.name || child.gender || child.birth_date || child.age || child.age_range);
}

// Children's first names are private by default. Strip them from any profile
// that doesn't belong to the current user. Server-side enforcement lives in
// migration 20260427120000_children_names_privacy.sql; this is defence-in-depth.
function stripChildrenNames(profile: any): any {
  if (!profile || typeof profile !== 'object') return profile;
  const stripList = (arr: any) =>
    Array.isArray(arr)
      ? arr.map((c: any) => {
          if (!c || typeof c !== 'object') return c;
          const { name: _name, ...rest } = c;
          return rest;
        })
      : arr;
  return {
    ...profile,
    children_data: stripList(profile.children_data),
    children: stripList(profile.children),
  };
}

function normalizeProfileData(profile: any) {
  if (!profile) return profile;

  const normalizedChildren = normalizeProfileChildren(profile.children_data ?? profile.children);
  const explicitChildrenCount = Number(profile.children_count ?? profile.childrenCount);
  const childrenCount = Number.isFinite(explicitChildrenCount)
    ? Math.max(0, explicitChildrenCount)
    : normalizedChildren.length;

  const birthdayValue = profile.birthday || profile.birthdate || profile.dateOfBirth || null;
  const cityValue = String(profile.city_name || profile.location || '').trim();
  const bioValue = String(profile.bio || profile.about || '').trim();
  const avatarValue = String(profile.avatar_url || profile.avatar || '').trim();

  return {
    ...profile,
    avatar_url: avatarValue,
    avatar: avatarValue,
    bio: bioValue,
    about: bioValue,
    location: cityValue,
    city_name: cityValue,
    interests: normalizeInterests(profile.interests),
    children_data: normalizedChildren,
    children: normalizedChildren,
    children_count: childrenCount,
    childrenCount: childrenCount,
    with_children: childrenCount > 0,
    birthday: birthdayValue,
    birthdate: profile.birthdate || birthdayValue,
    dateOfBirth: birthdayValue,
  };
}

async function resolveListingImageFields(listing: any, accessToken?: string | null) {
  if (!listing) return listing;

  const rawImages = Array.isArray(listing.images)
    ? listing.images.filter((img: any) => typeof img === 'string' && img.trim().length > 0)
    : [];
  const rawPrimary = typeof listing.image_url === 'string' ? listing.image_url : '';

  const resolvedImages = rawImages.length > 0
    ? (await Promise.all(rawImages.map((img: string) => resolveRenderableImage(img, 'listings', accessToken))))
      .filter(Boolean)
    : [];
  const resolvedPrimary = rawPrimary
    ? await resolveRenderableImage(rawPrimary, 'listings', accessToken)
    : (resolvedImages[0] || '');

  return {
    ...listing,
    images: resolvedImages.length > 0 ? resolvedImages : (resolvedPrimary ? [resolvedPrimary] : []),
    image_url: resolvedPrimary || null,
  };
}

async function resolveEventImageFields(event: any, accessToken?: string | null) {
  if (!event) return event;
  const rawImage = event.image_url || event.imageUrl || '';
  if (!rawImage) return event;

  const imageUrl = await resolveRenderableImage(String(rawImage), 'events', accessToken);
  return {
    ...event,
    image_url: imageUrl,
    imageUrl: imageUrl,
  };
}

function toTimestamp(value: any): number {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildEventStartTimestamp(event: any): number {
  if (!event) return 0;

  const rawDate = String(event.event_date || '').trim();
  const rawTime = String(event.event_time || '').trim();
  if (rawDate) {
    const normalizedTime = rawTime ? rawTime.slice(0, 8) : '00:00:00';
    const combined = new Date(`${rawDate}T${normalizedTime}`).getTime();
    if (!Number.isNaN(combined)) return combined;
  }

  return toTimestamp(event.date || event.createdAt || event.created_at);
}

const FORUM_DEBUG_PATTERNS = [
  /\bdebug\b/i,
  /\bprobe\d*\b/i,
  /\bautotest\b/i,
  /\bsmoke\b/i,
  /\btest question\b/i,
  /^ui flow question\b/i,
  /^ui debug question\b/i,
];

function hasForumDebugMarker(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text) return false;
  return FORUM_DEBUG_PATTERNS.some((pattern) => pattern.test(text));
}

function isRealForumQuestionRecord(record: any): boolean {
  if (!record) return false;
  if (hasForumDebugMarker(record.title) || hasForumDebugMarker(record.content)) return false;
  if (Array.isArray(record.tags) && record.tags.some((tag: string) => hasForumDebugMarker(tag))) return false;

  const authorRaw = Array.isArray(record.author) ? record.author[0] : record.author;
  const authorName = authorRaw?.name;
  if (hasForumDebugMarker(authorName)) return false;

  return true;
}

function getCachedUserProfile(userId: string): any | null {
  const entry = userProfileCache.get(userId);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    userProfileCache.delete(userId);
    return null;
  }
  return entry.value;
}

function setCachedUserProfile(userId: string, value: any) {
  userProfileCache.set(userId, {
    value,
    expiresAt: Date.now() + USER_PROFILE_CACHE_TTL_MS,
  });
}

function clearUserProfileCache(userId?: string) {
  if (userId) {
    userProfileCache.delete(userId);
    userProfileInFlight.delete(userId);
    return;
  }
  userProfileCache.clear();
  userProfileInFlight.clear();
}

function normalizeProfileUpdatePayload(data: any): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if ('name' in data) {
    payload.name = String(data.name || '').trim() || null;
  }

  if ('location' in data || 'city_name' in data || 'city' in data) {
    const cityValue = String(data.city_name || data.location || data.city || '').trim();
    // Only write city_name — profiles table has no "location" column
    payload.city_name = cityValue || null;
  }

  if ('latitude' in data && data.latitude !== undefined && data.latitude !== null) {
    const lat = Number(data.latitude);
    payload.latitude = Number.isFinite(lat) ? lat : null;
  }

  if ('longitude' in data && data.longitude !== undefined && data.longitude !== null) {
    const lng = Number(data.longitude);
    payload.longitude = Number.isFinite(lng) ? lng : null;
  }

  if ('bio' in data || 'about' in data) {
    payload.bio = String(data.bio || data.about || '').trim() || null;
  }

  if ('interests' in data) {
    payload.interests = normalizeInterests(data.interests);
  }

  if ('birthday' in data || 'birthdate' in data || 'dateOfBirth' in data) {
    const birthdayValue = String(data.birthday || data.birthdate || data.dateOfBirth || '').trim();
    payload.birthday = birthdayValue || null;
  }

  if ('children_data' in data || 'children' in data || 'children_count' in data || 'childrenCount' in data) {
    const normalizedChildren = normalizeProfileChildren(data.children_data ?? data.children);
    const explicitChildrenCount = Number(data.children_count ?? data.childrenCount);
    const childrenCount = Number.isFinite(explicitChildrenCount)
      ? Math.max(0, explicitChildrenCount)
      : normalizedChildren.length;

    payload.children_data = normalizedChildren;
    payload.children_count = childrenCount;
    payload.with_children = childrenCount > 0;
  }

  if ('currency' in data) {
    payload.currency = data.currency || 'BRL';
  }

  if ('privacy_show_location' in data) {
    payload.privacy_show_location = Boolean(data.privacy_show_location);
  }

  if ('privacy_show_in_search' in data) {
    payload.privacy_show_in_search = Boolean(data.privacy_show_in_search);
  }

  if ('avatar_url' in data || 'avatar' in data) {
    const avatarValue = String(data.avatar_url || data.avatar || '').trim();
    payload.avatar_url = avatarValue || null;
  }

  return payload;
}

function normalizeProfileFallbackPayload(data: any, dbPayload: Record<string, unknown>): Record<string, unknown> {
  return {
    ...data,
    ...dbPayload,
    avatar_url: dbPayload.avatar_url ?? data.avatar_url ?? data.avatar ?? '',
    avatar: dbPayload.avatar_url ?? data.avatar ?? data.avatar_url ?? '',
    dateOfBirth: dbPayload.birthday ?? data.dateOfBirth ?? null,
    childrenCount: dbPayload.children_count ?? data.childrenCount ?? 0,
    children: dbPayload.children_data ?? data.children ?? [],
    about: dbPayload.bio ?? data.about ?? '',
  };
}

// ============================================================
// FIX: apiCall с таймаутом 60 секунд для холодного старта
// Edge Function на Free плане засыпает и просыпается ~40 сек
// ============================================================

// Auth endpoints that legitimately return 401 — skip refresh-and-retry for these.
const NO_REFRESH_ENDPOINTS = new Set(['/auth/signin', '/auth/signup', '/auth/refresh', '/auth/logout']);

// B-41: gateway statuses that almost always mean "EF cold-start, try again
// in a moment". The keep-warm cron normally hides this, but the very first
// request after a long idle (or right after a deploy) can still race the
// boot. One quick retry papers over it without surfacing scary toasts.
const COLD_START_STATUSES = new Set([502, 503, 504]);
const COLD_START_RETRY_DELAY_MS = 1500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function apiCall(endpoint: string, options: RequestInit = {}, _isRetry = false): Promise<any> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.debug(`[apiCall] no access_token — using anon key for ${options.method || 'GET'} ${endpoint}`);
    headers['Authorization'] = `Bearer ${publicAnonKey}`;
  }

  const url = `${API_BASE}${endpoint}`;
  const method = options.method || 'GET';
  console.log(`[apiCall] → ${method} ${endpoint}`);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    console.log(`[apiCall] ← ${method} ${endpoint} → HTTP ${response.status}`);

    // 401 interceptor: silently refresh the token and retry once.
    // Skipped on auth endpoints (they can't refresh their way out of a 401)
    // and on retries (prevent infinite loops).
    if (response.status === 401 && !_isRetry && !NO_REFRESH_ENDPOINTS.has(endpoint)) {
      console.debug(`[apiCall] 401 on ${endpoint} — attempting token refresh`);
      const refreshed = await authAPI.refreshSession();
      if (refreshed?.access_token) {
        console.debug(`[apiCall] refresh succeeded — retrying ${endpoint}`);
        clearTimeout(timeoutId);
        return apiCall(endpoint, options, true);
      }
      // Refresh failed — sign out so the user can re-authenticate cleanly.
      console.warn(`[apiCall] refresh failed after 401 on ${endpoint} — clearing auth`);
      authAPI.clearAuth();
      window.location.replace('/login');
      throw new Error('Session expired. Please log in again.');
    }

    // Cold-start: retry once on 502/503/504. Idempotent for GET, and the
    // window is small enough that POST/PUT/DELETE from a transient gateway
    // failure is also worth retrying once — the alternative is the user
    // pressing the button twice and ending up with a duplicate.
    if (COLD_START_STATUSES.has(response.status) && !_isRetry) {
      console.warn(`[apiCall] HTTP ${response.status} on ${endpoint} — retrying once after cold-start delay`);
      clearTimeout(timeoutId);
      await sleep(COLD_START_RETRY_DELAY_MS);
      return apiCall(endpoint, options, true);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[apiCall] Ошибка ${response.status} для ${method} ${endpoint}:`, errorText);
      throw new Error(`API Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      console.error(`[apiCall] Таймаут (60s) для ${method} ${endpoint}`);
      throw new Error('Сервер не отвечает. Попробуйте ещё раз.');
    }
    if (!(err?.message?.startsWith('API Error'))) {
      console.error(`[apiCall] Сетевая ошибка для ${method} ${endpoint}:`, err);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Single-flight lock: prevents concurrent refresh calls from burning the rotation token.
// All callers that arrive while a refresh is in-flight receive the same promise.
let _refreshPromise: Promise<any> | null = null;

// Auth API
export const authAPI = {
  signup: async (data: { email: string; password: string; name: string; phone?: string }) => {
    const result = await apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (result?.user) {
      result.user = await resolveProfileImageFields(result.user, result.access_token || null);
    }
    if (result.access_token) {
      localStorage.setItem('access_token', result.access_token);
    }
    if (result.refresh_token) {
      localStorage.setItem('refresh_token', result.refresh_token);
    }
    if (result.user?.id) {
      localStorage.setItem('user_id', result.user.id);
    }
    return result;
  },

  login: async (email: string, password: string) => {
    const result = await apiCall('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (result?.user) {
      result.user = await resolveProfileImageFields(result.user, result.access_token || null);
    }
    if (result.access_token) {
      localStorage.setItem('access_token', result.access_token);
    }
    // Always save refresh_token so session survives Telegram WebApp close/reopen
    if (result.refresh_token) {
      localStorage.setItem('refresh_token', result.refresh_token);
    }
    if (result.user?.id) {
      localStorage.setItem('user_id', result.user.id);
    }
    return result;
  },

  signin: async (email: string, password: string) => {
    return authAPI.login(email, password);
  },

  logout: async () => {
    const result = await apiCall('/auth/logout', {
      method: 'POST',
    });
    authAPI.clearAuth();
    return result;
  },

  getSession: async () => {
    const result = await apiCall('/auth/session');
    if (result?.user) {
      result.user = await resolveProfileImageFields(result.user);
    }
    return result;
  },

  refreshSession: async () => {
    if (_refreshPromise) return _refreshPromise;

    _refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return null;
        const result = await apiCall('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!result?.access_token) return null;
        localStorage.setItem('access_token', result.access_token);
        if (result.refresh_token) {
          localStorage.setItem('refresh_token', result.refresh_token);
        }
        return result;
      } catch (error) {
        console.error('refreshSession failed:', error);
        return null;
      } finally {
        _refreshPromise = null;
      }
    })();

    return _refreshPromise;
  },

  /** Step 1 of password reset: look up user by email + send code */
  forgotPassword: async (email: string, method: 'email' | 'telegram' = 'email') => {
    return apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, method }),
    });
  },

  /** Step 2 of password reset: verify code + set new password */
  resetPassword: async (resetToken: string, code: string, newPassword: string) => {
    return apiCall('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ reset_token: resetToken, code, new_password: newPassword }),
    });
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    return apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  getCurrentUserId: (): string | null => {
    return localStorage.getItem('user_id');
  },

  setCurrentUserId: (userId: string) => {
    localStorage.setItem('user_id', userId);
  },

  clearAuth: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    clearSupabaseClientCache();
  },

  deleteAccount: async () => {
    await apiCall('/auth/account', { method: 'DELETE' });
    authAPI.clearAuth();
  },
};

// User API
export const userAPI = {
  getUser: async (userId: string) => {
    const data = await apiCall(`/users/${userId}`);
    return data as { stats?: { products: number; events: number; answers: number } } & Record<string, any>;
  },

  updateUser: async (userId: string, data: any) => {
    return userAPI.updateProfile(userId, data);
  },

  // ============================================================
  // FIX: getProfile читает напрямую из Supabase (без Edge Function)
  // Избегаем холодного старта для критичных запросов профиля
  // ============================================================
  getProfile: async (userId: string, options?: { forceRefresh?: boolean }) => {
    if (!userId) return null;

    if (!options?.forceRefresh) {
      const cached = getCachedUserProfile(userId);
      if (cached) return cached;

      const inFlight = userProfileInFlight.get(userId);
      if (inFlight) return inFlight;
    }

    const isSelf = authAPI.getCurrentUserId() === userId;
    const applyPrivacy = (profile: any) => (isSelf ? profile : stripChildrenNames(profile));

    const requestPromise = (async () => {
      try {
        // Сначала пробуем прямой запрос к Supabase (быстро, без холодного старта)
        const supabase = getSupabaseClient(getAuthToken());
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (!error && data) {
          const resolved = await resolveProfileImageFields(applyPrivacy(data));
          setCachedUserProfile(userId, resolved);
          return resolved;
        }
      } catch {
        // fallback to Edge Function
      }

      // Fallback: Edge Function
      const data = await apiCall(`/users/${userId}`);
      const resolved = await resolveProfileImageFields(applyPrivacy(data));
      setCachedUserProfile(userId, resolved);
      return resolved;
    })();

    if (!options?.forceRefresh) {
      userProfileInFlight.set(userId, requestPromise);
    }

    try {
      return await requestPromise;
    } finally {
      userProfileInFlight.delete(userId);
    }
  },

  updateProfile: async (userId: string, data: any) => {
    // All profile updates go exclusively through the Edge Function.
    // The Edge Function owns the DB write (profiles table) and KV sync.
    // Sending raw data so the Edge Function applies its own normalization.
    const updated = await apiCall(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
    const resolved = await resolveProfileImageFields(updated);
    clearUserProfileCache(userId);
    setCachedUserProfile(userId, resolved);
    return resolved;
  },

  searchUsers: async (filters?: any) => {
    const params = filters ? `?${new URLSearchParams(filters)}` : '';
    const users = await apiCall(`/users${params}`);
    if (!Array.isArray(users)) return [];
    return Promise.all(users.map((user: any) => resolveProfileImageFields(user)));
  },

  // Routes all settings writes through EF PUT /users/:id (service_role, bypasses RLS).
  // Fields: notification_push, notification_email, language, privacy_show_location,
  //         privacy_show_in_search, currency.
  updateSettings: async (
    userId: string,
    data: Partial<{
      notification_push: boolean;
      notification_email: boolean;
      language: string;
      privacy_show_location: boolean;
      privacy_show_in_search: boolean;
      currency: string;
    }>,
  ): Promise<void> => {
    await userAPI.updateProfile(userId, data);
  },

  getCurrentUser: async () => {
    const userId = authAPI.getCurrentUserId();
    if (userId) {
      // Пробуем прямой запрос к Supabase
      try {
        const supabase = getSupabaseClient(getAuthToken());
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (!error && data) {
          const resolved = await resolveProfileImageFields(data);
          setCachedUserProfile(userId, resolved);
          return resolved;
        }
      } catch {
        // fallback
      }
    }

    const data = await apiCall('/users/me');
    const resolved = await resolveProfileImageFields(data);
    if (resolved?.id) {
      setCachedUserProfile(resolved.id, resolved);
    }
    return resolved;
  },

  // ============================================================
  // FIX: getAllUsers читает напрямую из Supabase profiles таблицы
  // ============================================================
  getAllUsers: async () => {
    try {
      const currentUserId = authAPI.getCurrentUserId();
      const supabase = getSupabaseClient(getAuthToken());
      const [profilesRes, blockedIds] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('is_test_account', false)
          .neq('privacy_show_in_search', false)
          .not('name', 'is', null)
          .neq('name', '')
          .order('created_at', { ascending: false }),
        blockAPI.getBlockedIds(),
      ]);
      const { data, error } = profilesRes;

      if (!error && data) {
        const hasAnyContent = (u: any) => {
          const childrenCount =
            u?.children_count ??
            (Array.isArray(u?.children_data) ? u.children_data.length : 0);
          const interestsCount = Array.isArray(u?.interests) ? u.interests.length : 0;
          return (
            childrenCount > 0 ||
            interestsCount > 0 ||
            (typeof u?.city_name === 'string' && u.city_name.trim().length > 0) ||
            (typeof u?.bio === 'string' && u.bio.trim().length > 0) ||
            u?.is_pregnant === true
          );
        };
        const filtered = (currentUserId
          ? data.filter((u: any) => u.id !== currentUserId)
          : data
        )
          .filter((u: any) => !blockedIds.has(String(u.id)))
          .filter((u: any) => typeof u.name === 'string' && u.name.trim().length > 0)
          .filter(hasAnyContent);
        return Promise.all(filtered.map((user: any) => resolveProfileImageFields(stripChildrenNames(user))));
      }
    } catch {
      // fallback
    }

    const users = await apiCall('/users');
    if (!Array.isArray(users)) return [];
    const blockedIds = await blockAPI.getBlockedIds();
    const completeUsers = users
      .filter((u: any) => typeof u?.name === 'string' && u.name.trim().length > 0)
      .filter((u: any) => !blockedIds.has(String(u?.id)));
    return Promise.all(completeUsers.map((user: any) => resolveProfileImageFields(stripChildrenNames(user))));
  },

  // ============================================================
  // getNearbyUsers — calls get_users_nearby RPC (server-side
  // bounding box + haversine, privacy-filtered). Falls back to
  // client-side filtering if the RPC is unavailable.
  // ============================================================
  getNearbyUsers: async (lat: number, lng: number, radiusKm: number = 5) => {
    const currentUserId = authAPI.getCurrentUserId();
    try {
      const supabase = getSupabaseClient(getAuthToken());
      const [rpcRes, blockedIds] = await Promise.all([
        supabase.rpc('get_users_nearby', {
          p_lat: lat,
          p_lng: lng,
          p_radius_km: radiusKm,
          p_exclude_id: currentUserId || null,
        }),
        blockAPI.getBlockedIds(),
      ]);
      const { data, error } = rpcRes;
      if (error) throw error;
      if (data) {
        const filtered = (data as any[]).filter((u: any) => !blockedIds.has(String(u.id)));
        return Promise.all(filtered.map((u: any) => resolveProfileImageFields(stripChildrenNames(u))));
      }
      return [];
    } catch (rpcErr) {
      console.warn('[getNearbyUsers] RPC unavailable, falling back to client filter:', rpcErr);
      // Fallback: fetch all users and filter by haversine client-side
      try {
        const supabase = getSupabaseClient(getAuthToken());
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('is_test_account', false)
          .not('latitude', 'is', null)
          .not('longitude', 'is', null)
          .neq('privacy_show_in_search', false)
          .neq('privacy_show_location', false);

        if (error || !data) return [];

        const R = 6371;
        const haversine = (lat2: number, lng2: number) => {
          const dLat = ((lat2 - lat) * Math.PI) / 180;
          const dLng = ((lng2 - lng) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLng / 2) ** 2;
          return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        const blockedIds = await blockAPI.getBlockedIds();
        const nearby = data
          .filter((u: any) => u.id !== currentUserId)
          .filter((u: any) => !blockedIds.has(String(u.id)))
          .map((u: any) => ({
            ...u,
            distance_km: haversine(Number(u.latitude), Number(u.longitude)),
          }))
          .filter((u: any) => u.distance_km <= radiusKm)
          .sort((a: any, b: any) => a.distance_km - b.distance_km)
          .slice(0, 100);

        return Promise.all(nearby.map((u: any) => resolveProfileImageFields(stripChildrenNames(u))));
      } catch {
        return [];
      }
    }
  },

  saveUser: async (targetUserId: string) => {
    return apiCall(`/users/${targetUserId}/save`, { method: 'POST' });
  },

  unsaveUser: async (targetUserId: string) => {
    return apiCall(`/users/${targetUserId}/save`, { method: 'DELETE' });
  },

  getSavedUsers: async () => {
    const users = await apiCall('/users/saved');
    if (!Array.isArray(users)) return [];
    return Promise.all(users.map((user: any) => resolveProfileImageFields(user)));
  },
};

// Event API
export const eventAPI = {
  getById: async (eventId: string) => {
    return eventAPI.getEvent(eventId);
  },

  // ============================================================
  // FIX: getEvents читает напрямую из Supabase events таблицы
  // ============================================================
  getEvents: async (filters?: any, options?: { resolveImages?: boolean }) => {
    try {
      const supabase = getSupabaseClient(getAuthToken());
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'active')
        .order('event_date', { ascending: true });

      if (!error && data) {
        const shouldResolveImages = options?.resolveImages !== false;
        if (shouldResolveImages) {
          return Promise.all(data.map((event: any) => resolveEventImageFields(event)));
        }
        return data;
      }
    } catch {
      // fallback
    }

    const params = filters ? `?${new URLSearchParams(filters)}` : '';
    const events = await apiCall(`/events${params}`);
    if (!Array.isArray(events)) return [];
    const shouldResolveImages = options?.resolveImages !== false;
    const preparedEvents = shouldResolveImages
      ? await Promise.all(events.map((event: any) => resolveEventImageFields(event)))
      : events;
    return preparedEvents.sort((a: any, b: any) => buildEventStartTimestamp(a) - buildEventStartTimestamp(b));
  },

  getEvent: async (eventId: string) => {
    try {
      const supabase = getSupabaseClient(getAuthToken());
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .maybeSingle();
      if (!error && data) return resolveEventImageFields(data);
    } catch {
      // fallback
    }
    const event = await apiCall(`/events/${eventId}`);
    return resolveEventImageFields(event);
  },

  createEvent: async (data: any) => {
    return apiCall('/events', { method: 'POST', body: JSON.stringify(data) });
  },

  updateEvent: async (eventId: string, data: any) => {
    return apiCall(`/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  deleteEvent: async (eventId: string) => {
    return apiCall(`/events/${eventId}`, { method: 'DELETE' });
  },

  rsvp: async (eventId: string, status: 'going' | 'interested' | 'not_going') => {
    return apiCall(`/events/${eventId}/rsvp`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  },

  join: async (eventId: string) => eventAPI.rsvp(eventId, 'going'),
  leave: async (eventId: string) => eventAPI.rsvp(eventId, 'not_going'),

  getComments: async (eventId: string) => {
    return apiCall(`/events/${eventId}/comments`);
  },

  addComment: async (eventId: string, content: string) => {
    return apiCall(`/events/${eventId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  deleteComment: async (eventId: string, commentId: string) => {
    return apiCall(`/events/${eventId}/comments/${commentId}`, { method: 'DELETE' });
  },

  getParticipants: async (eventId: string) => {
    return apiCall(`/events/${eventId}/participants`);
  },

  removeParticipant: async (eventId: string, userId: string) => {
    return apiCall(`/events/${eventId}/participants/${userId}`, { method: 'DELETE' });
  },

  cancelEvent: async (eventId: string) => {
    return apiCall(`/events/${eventId}/cancel`, { method: 'PATCH' });
  },

  getChatStatus: async (eventId: string) => {
    return apiCall(`/events/${eventId}/chat-status`);
  },

  getPhotos: async (eventId: string) => {
    return apiCall(`/events/${eventId}/photos`);
  },

  addPhoto: async (eventId: string, storagePath: string, caption?: string) => {
    return apiCall(`/events/${eventId}/photos`, {
      method: 'POST',
      body: JSON.stringify({ storage_path: storagePath, caption }),
    });
  },

  deletePhoto: async (eventId: string, photoId: string) => {
    return apiCall(`/events/${eventId}/photos/${photoId}`, { method: 'DELETE' });
  },
};

// Forum/Question API
export const questionAPI = {
  getById: async (questionId: string) => {
    return questionAPI.getQuestion(questionId);
  },

  getQuestions: async (params?: { tab?: string; lat?: number; lng?: number }) => {
    const qs = new URLSearchParams();
    if (params?.tab) qs.set('tab', params.tab);
    if (params?.lat !== undefined) qs.set('lat', String(params.lat));
    if (params?.lng !== undefined) qs.set('lng', String(params.lng));
    const queryString = qs.toString();
    const endpoint = `/questions${queryString ? `?${queryString}` : ''}`;

    try {
      const questions = await apiCall(endpoint);
      if (Array.isArray(questions)) {
        return questions.filter(isRealForumQuestionRecord);
      }
    } catch {
      // fallback
    }

    try {
      const supabase = getSupabaseClient(getAuthToken());
      const { data, error } = await supabase
        .from('forum_questions')
        .select('*, author:profiles(id, name, avatar_url), category:categories(id, slug)')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (!error && data) {
        return data.filter(isRealForumQuestionRecord);
      }
    } catch {
      // fallback below
    }

    return [];
  },

  getQuestion: async (questionId: string) => {
    try {
      const question = await apiCall(`/forum/questions/${questionId}`);
      if (question && isRealForumQuestionRecord(question)) {
        return question;
      }
      return null;
    } catch {
      // fallback
    }

    try {
      const supabase = getSupabaseClient(getAuthToken());
      const { data: question, error } = await supabase
        .from('forum_questions')
        .select('*, author:profiles(id, name, avatar_url)')
        .eq('id', questionId)
        .maybeSingle();

      if (!error && question && isRealForumQuestionRecord(question)) {
        const { data: answers } = await supabase
          .from('forum_answers')
          .select('*, author:profiles(id, name, avatar_url)')
          .eq('question_id', questionId)
          .eq('status', 'active')
          .order('created_at', { ascending: true });

        return { ...question, answers: answers || [] };
      }
    } catch {
      // fallback
    }
    return null;
  },

  getComments: async (questionId: string) => {
    const question = await questionAPI.getQuestion(questionId);
    return question?.answers || [];
  },

  createQuestion: async (data: any) => {
    try {
      return await apiCall('/questions', { method: 'POST', body: JSON.stringify(data) });
    } catch (error: any) {
      // Retry once for transient cold-start/network failures to improve publish reliability.
      const message = String(error?.message || '').toLowerCase();
      const isTransient = message.includes('сервер не отвечает') || message.includes('network') || message.includes('failed to fetch');
      if (!isTransient) throw error;
      return apiCall('/questions', { method: 'POST', body: JSON.stringify(data) });
    }
  },

  updateQuestion: async (questionId: string, data: any) => {
    return apiCall(`/forum/questions/${questionId}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  deleteQuestion: async (questionId: string) => {
    return apiCall(`/forum/questions/${questionId}`, { method: 'DELETE' });
  },

  addComment: async (questionId: string, content: string) => {
    return apiCall(`/forum/questions/${questionId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  createAnswer: async (questionId: string, text: string) => {
    return apiCall(`/questions/${questionId}/answers`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  likeQuestion: async (questionId: string) => {
    return apiCall(`/forum/questions/${questionId}/like`, { method: 'POST' });
  },

  saveQuestion: async (questionId: string) => {
    return apiCall('/questions/saved', {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId }),
    });
  },

  unsaveQuestion: async (questionId: string) => {
    return apiCall(`/questions/saved/${questionId}`, { method: 'DELETE' });
  },

  getSavedQuestions: async () => {
    return apiCall('/questions/saved');
  },

  deleteAnswer: async (questionId: string, answerId: string) => {
    return apiCall(`/forum/questions/${questionId}/answers/${answerId}`, { method: 'DELETE' });
  },
};

// Marketplace/Listing API
export const listingAPI = {
  // ============================================================
  // FIX: getListings читает напрямую из Supabase
  // ============================================================
  getListings: async (filters?: any, options?: { resolveImages?: boolean }) => {
    const shouldResolveImages = options?.resolveImages !== false;

    try {
      const supabase = getSupabaseClient(getAuthToken());

      // Fetch user-created listings from marketplace_products
      const { data: mpData } = await supabase
        .from('marketplace_products')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      // Fetch Telegram-sourced listings from listings table
      const { data: tgData } = await supabase
        .from('listings')
        .select('id, created_at, title, description, price, location, category, author_username, photo_url, original_text')
        .order('created_at', { ascending: false })
        .limit(200);

      // Normalise Telegram listings to the same shape as marketplace_products
      const tgNormalised = (tgData || []).map((item: any) => ({
        ...item,
        category: normalizeCategoryToSlug(item.category),
        source: 'telegram' as const,
        image_url: item.photo_url || null,
        city_name: item.location || null,
        // Build a t.me deep-link from author_username
        telegram_url: item.author_username
          ? `https://t.me/${item.author_username}`
          : null,
      }));

      const mpNormalised = (mpData || []).map((item: any) => ({
        ...item,
        category: normalizeCategoryToSlug(item.category),
        source: 'user' as const,
      }));

      const merged = [...mpNormalised, ...tgNormalised].sort(
        (a, b) => toTimestamp(b.created_at) - toTimestamp(a.created_at)
      );

      if (shouldResolveImages) {
        return Promise.all(merged.map((listing: any) => resolveListingImageFields(listing)));
      }
      return merged;
    } catch {
      // fallback to edge function
    }

    const params = filters ? `?${new URLSearchParams(filters)}` : '';
    const listings = await apiCall(`/marketplace/listings${params}`);
    if (!Array.isArray(listings)) return [];
    const preparedListings = shouldResolveImages
      ? await Promise.all(listings.map((listing: any) => resolveListingImageFields(listing)))
      : listings;
    return preparedListings.sort(
      (a: any, b: any) =>
        toTimestamp(b.createdAt || b.created_at) - toTimestamp(a.createdAt || a.created_at)
    );
  },

  getListing: async (listingId: string) => {
    const isMp = isUuid(listingId);
    try {
      const supabase = getSupabaseClient(getAuthToken());

      if (isMp) {
        const { data, error } = await supabase
          .from('marketplace_products')
          .select('*')
          .eq('id', listingId)
          .maybeSingle();
        if (!error && data) {
          return resolveListingImageFields({
            ...data,
            category: normalizeCategoryToSlug(data.category),
            source: 'user',
          });
        }
      } else {
        const numericId = Number(listingId);
        if (!Number.isNaN(numericId)) {
          const { data, error } = await supabase
            .from('listings')
            .select('id, created_at, title, description, price, location, category, author_username, photo_url, original_text')
            .eq('id', numericId)
            .maybeSingle();
          if (!error && data) {
            return resolveListingImageFields({
              ...data,
              category: normalizeCategoryToSlug(data.category),
              source: 'telegram',
              image_url: data.photo_url || null,
              city_name: data.location || null,
              telegram_url: data.author_username
                ? `https://t.me/${data.author_username}`
                : null,
            });
          }
        }
      }
    } catch {
      // fallback
    }
    const qs = isMp ? '' : '?source=tg';
    const listing = await apiCall(`/marketplace/listings/${listingId}${qs}`);
    return resolveListingImageFields(listing);
  },

  getMyListings: async () => {
    const userId = authAPI.getCurrentUserId();
    if (!userId) return [];
    const supabase = getSupabaseClient(getAuthToken());
    const { data, error } = await supabase
      .from('marketplace_products')
      .select('*')
      .eq('seller_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return await Promise.all((data || []).map((item: any) => resolveListingImageFields({
      ...item,
      category: normalizeCategoryToSlug(item.category),
      source: 'user' as const,
    })));
  },

  createListing: async (data: any) => {
    return apiCall('/marketplace/listings', { method: 'POST', body: JSON.stringify(data) });
  },

  updateListing: async (listingId: string, data: any) => {
    return apiCall(`/marketplace/listings/${listingId}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  deleteListing: async (listingId: string) => {
    return apiCall(`/marketplace/listings/${listingId}`, { method: 'DELETE' });
  },

  saveListing: async (listingId: string) => {
    return apiCall(`/marketplace/listings/${listingId}/save`, { method: 'POST' });
  },

  unsaveListing: async (listingId: string) => {
    return apiCall(`/marketplace/listings/${listingId}/save`, { method: 'DELETE' });
  },

  getSavedListings: async () => {
    return apiCall('/marketplace/listings/saved');
  },

  getComments: async (listingId: string) => {
    const qs = isUuid(listingId) ? '' : '?source=tg';
    return apiCall(`/marketplace/listings/${listingId}/comments${qs}`);
  },

  addComment: async (listingId: string, content: string) => {
    const qs = isUuid(listingId) ? '' : '?source=tg';
    return apiCall(`/marketplace/listings/${listingId}/comments${qs}`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },
};

// Chat API — уже работает через Supabase напрямую, оставляем как есть
export const chatAPI = {
  getChats: async (accessToken?: string) => {
    const currentUserId = authAPI.getCurrentUserId();
    if (!currentUserId) return [];

    let token = accessToken || getAuthToken();

    // If token is expired, try to refresh before making the request.
    const isTokenExpired = (t: string | null | undefined) => {
      if (!t) return true;
      try {
        const { exp } = JSON.parse(atob(t.split('.')[1]));
        return typeof exp === 'number' && exp * 1000 < Date.now() + 10_000;
      } catch { return false; }
    };

    if (isTokenExpired(token)) {
      try {
        const refreshed = await authAPI.refreshSession();
        if (refreshed?.access_token) {
          token = refreshed.access_token;
        }
      } catch {
        // Refresh failed — proceed with expired token; Supabase will 401 cleanly.
      }
    }

    const supabase = getSupabaseClient(token);
    const [{ data: chats, error }, { data: hiddenRows }] = await Promise.all([
      supabase
        .from('chats')
        .select('id, participant1_id, participant2_id, created_at')
        .or(`participant1_id.eq.${currentUserId},participant2_id.eq.${currentUserId}`)
        .order('created_at', { ascending: false }),
      // Hidden chats — table created in migration 20260502010000_hidden_chats.sql.
      // RLS guarantees we only see our own rows. If the migration isn't applied
      // yet the call returns an error and we treat the set as empty.
      supabase
        .from('hidden_chats')
        .select('chat_id, hidden_at')
        .eq('user_id', currentUserId),
    ]);
    const hiddenAtById = new Map<string, string>();
    for (const row of (hiddenRows as any[] | null) || []) {
      if (row?.chat_id && row?.hidden_at) hiddenAtById.set(String(row.chat_id), String(row.hidden_at));
    }

    if (error) throw new Error(error.message || 'Failed to fetch chats');
    if (!chats || chats.length === 0) return [];

    const otherUserIds = Array.from(
      new Set(
        chats
          .map((chat: any) =>
            chat.participant1_id === currentUserId ? chat.participant2_id : chat.participant1_id
          )
          .filter(Boolean)
      )
    );

    const profilesById = new Map<string, any>();
    if (otherUserIds.length > 0) {
      const { data: otherUsers, error: otherUsersError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url, city_name')
        .in('id', otherUserIds);

      if (otherUsersError) throw new Error(otherUsersError.message || 'Failed to fetch chat participants');

      const resolvedUsers = await Promise.all(
        (otherUsers || []).map((profile: any) => resolveProfileImageFields(profile, accessToken || null))
      );
      resolvedUsers.forEach((profile: any) => {
        if (profile?.id) profilesById.set(profile.id, profile);
      });
    }

    const chatIds = chats.map((c: any) => c.id);

    // Batch-fetch last messages and unread counts in two queries (not N+1)
    const [{ data: allLastMessages }, { data: unreadRows }] = await Promise.all([
      supabase
        .from('messages')
        .select('id, chat_id, content, sender_id, created_at')
        .in('chat_id', chatIds)
        .order('created_at', { ascending: false }),
      supabase
        .from('messages')
        .select('chat_id')
        .in('chat_id', chatIds)
        .neq('sender_id', currentUserId)
        .eq('is_read', false),
    ]);

    // Build lookup maps from the batch results
    const lastMessageByChatId = new Map<string, any>();
    for (const msg of allLastMessages || []) {
      if (!lastMessageByChatId.has(msg.chat_id)) {
        lastMessageByChatId.set(msg.chat_id, msg);
      }
    }

    const unreadCountByChatId = new Map<string, number>();
    for (const row of unreadRows || []) {
      unreadCountByChatId.set(row.chat_id, (unreadCountByChatId.get(row.chat_id) || 0) + 1);
    }

    const result = chats
      .map((chat: any) => {
        const otherUserId = chat.participant1_id === currentUserId
          ? chat.participant2_id
          : chat.participant1_id;

        const lastMessage = lastMessageByChatId.get(chat.id);
        const resolvedOtherUser = otherUserId ? profilesById.get(otherUserId) : null;

        return {
          id: chat.id,
          participant1_id: chat.participant1_id,
          participant2_id: chat.participant2_id,
          created_at: chat.created_at,
          otherUser: resolvedOtherUser
            ? { ...resolvedOtherUser, avatar: resolvedOtherUser.avatar_url || '', location: resolvedOtherUser.city_name || '' }
            : null,
          lastMessage: lastMessage?.content || '',
          lastMessageAt: lastMessage?.created_at || chat.created_at,
          unreadCount: unreadCountByChatId.get(chat.id) || 0,
        };
      })
      // Hidden chats stay hidden until the other side sends a new message:
      // a hidden_chats row is dropped from the visible list iff the most recent
      // message is older than (or equal to) the moment we hid it.
      .filter((chat: any) => {
        const hiddenAt = hiddenAtById.get(chat.id);
        if (!hiddenAt) return true;
        const lastMs = new Date(chat.lastMessageAt).getTime();
        const hiddenMs = new Date(hiddenAt).getTime();
        return Number.isFinite(lastMs) && Number.isFinite(hiddenMs) && lastMs > hiddenMs;
      });

    return result.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
  },

  getChat: async (chatId: string, accessToken?: string) => {
    const currentUserId = authAPI.getCurrentUserId();
    const supabase = getSupabaseClient(accessToken || getAuthToken());

    // Single query with JOIN — no N+1
    const { data: chat, error } = await supabase
      .from('chats')
      .select(`
        id, participant1_id, participant2_id, created_at,
        p1:profiles!chats_participant1_id_fkey(id, name, avatar_url, city_name),
        p2:profiles!chats_participant2_id_fkey(id, name, avatar_url, city_name)
      `)
      .eq('id', chatId)
      .maybeSingle();

    if (error) {
      // Fallback: two separate queries if FK join fails (e.g. no FK constraint name)
      const { data: chatRaw, error: err2 } = await supabase
        .from('chats')
        .select('id, participant1_id, participant2_id, created_at')
        .eq('id', chatId)
        .maybeSingle();

      if (err2) throw new Error(err2.message || 'Failed to fetch chat');
      if (!chatRaw) return null;

      const otherUserId = chatRaw.participant1_id === currentUserId
        ? chatRaw.participant2_id
        : chatRaw.participant1_id;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, name, avatar_url, city_name')
        .eq('id', otherUserId)
        .maybeSingle();
      const resolved = await resolveProfileImageFields(profile, accessToken || null);
      return {
        ...chatRaw,
        otherUser: resolved
          ? { ...resolved, avatar: resolved.avatar_url || '', location: resolved.city_name || '' }
          : null,
      };
    }

    if (!chat) return null;

    const isP1 = chat.participant1_id === currentUserId;
    const otherUserRaw = isP1 ? (chat as any).p2 : (chat as any).p1;
    const resolvedOtherUser = await resolveProfileImageFields(otherUserRaw, accessToken || null);

    return {
      id: chat.id,
      participant1_id: chat.participant1_id,
      participant2_id: chat.participant2_id,
      created_at: chat.created_at,
      otherUser: resolvedOtherUser
        ? { ...resolvedOtherUser, avatar: resolvedOtherUser.avatar_url || '', location: resolvedOtherUser.city_name || '' }
        : null,
    };
  },

  getMessages: async (
    chatId: string,
    accessToken?: string,
    { limit = 50, before }: { limit?: number; before?: string } = {}
  ) => {
    const supabase = getSupabaseClient(accessToken || getAuthToken());

    let query = supabase
      .from('messages')
      .select('id, chat_id, sender_id, content, image_url, created_at, is_read, read_at')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: false }) // newest first for cursor pagination
      .limit(limit);

    // Cursor-based pagination: fetch messages older than `before` timestamp
    if (before) {
      query = query.lt('created_at', before);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message || 'Failed to fetch messages');

    // Reverse to chronological order for display
    return (data || []).reverse().map((message: any) => ({
      id: message.id,
      chatId: message.chat_id,
      senderId: message.sender_id,
      text: message.content || '',
      imageUrl: message.image_url || '',
      createdAt: message.created_at,
      isRead: message.is_read ?? false,
      readAt: message.read_at ?? null,
    }));
  },

  sendMessage: async (
    chatId: string,
    content: string,
    accessToken?: string,
    options?: { imageUrl?: string },
  ) => {
    const senderId = authAPI.getCurrentUserId();
    if (!senderId) throw new Error('Not authenticated');

    const text = content.trim();
    const imageUrl = options?.imageUrl?.trim() || '';
    // Allow image-only messages (text empty when imageUrl is set).
    if (!text && !imageUrl) throw new Error('Message cannot be empty');

    const supabase = getSupabaseClient(accessToken || getAuthToken());

    const insertPayload: Record<string, any> = {
      chat_id: chatId,
      sender_id: senderId,
      content: text,
    };
    if (imageUrl) insertPayload.image_url = imageUrl;

    const { data, error } = await supabase
      .from('messages')
      .insert(insertPayload)
      .select('id, chat_id, sender_id, content, image_url, created_at')
      .single();

    if (error) throw new Error(error.message || 'Failed to send message');

    return {
      id: data.id,
      chatId: data.chat_id,
      senderId: data.sender_id,
      text: data.content || '',
      imageUrl: data.image_url || '',
      createdAt: data.created_at,
    };
  },

  createOrGetChat: async (participantId: string, accessToken?: string) => {
    const supabase = getSupabaseClient(accessToken || getAuthToken());
    const { data, error } = await supabase.rpc('get_or_create_chat', {
      other_user_id: participantId,
    });

    if (error) throw new Error(error.message || 'Failed to create chat');
    return data as string;
  },

  createChat: async (participantId: string, accessToken?: string) => {
    const chatId = await chatAPI.createOrGetChat(participantId, accessToken);
    return { id: chatId };
  },

  deleteMessage: async (messageId: string) => {
    return apiCall(`/messages/${messageId}`, { method: 'DELETE' });
  },

  markAsRead: async (chatId: string) => {
    const currentUserId = authAPI.getCurrentUserId();
    if (!currentUserId) return;
    const supabase = getSupabaseClient(getAuthToken());
    // Mark all messages in this chat from the other participant as read
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('chat_id', chatId)
      .neq('sender_id', currentUserId)
      .eq('is_read', false);
    if (error) {
      console.error('[markAsRead] failed:', error.message);
    }
  },

  // F-05: hide a chat from the current user's list. The other participant
  // is unaffected. The chat reappears for us when a newer message arrives
  // (filter logic in getChats compares lastMessageAt vs hidden_at).
  hideChat: async (chatId: string) => {
    const currentUserId = authAPI.getCurrentUserId();
    if (!currentUserId) throw new Error('Not authenticated');
    const supabase = getSupabaseClient(getAuthToken());
    const { error } = await supabase
      .from('hidden_chats')
      .upsert(
        { user_id: currentUserId, chat_id: chatId, hidden_at: new Date().toISOString() },
        { onConflict: 'user_id,chat_id' },
      );
    if (error) throw new Error(error.message || 'Failed to hide chat');
  },

  unhideChat: async (chatId: string) => {
    const currentUserId = authAPI.getCurrentUserId();
    if (!currentUserId) throw new Error('Not authenticated');
    const supabase = getSupabaseClient(getAuthToken());
    const { error } = await supabase
      .from('hidden_chats')
      .delete()
      .eq('user_id', currentUserId)
      .eq('chat_id', chatId);
    if (error) throw new Error(error.message || 'Failed to unhide chat');
  },
};

// Notification API
export const notificationAPI = {
  getAll: async () => {
    return notificationAPI.getNotifications();
  },

  getNotifications: async () => {
    return apiCall('/notifications');
  },

  markAsRead: async (notificationId: string) => {
    return apiCall(`/notifications/${notificationId}/read`, { method: 'PUT' });
  },

  markAllAsRead: async () => {
    return apiCall('/notifications/read-all', { method: 'POST' });
  },

  deleteNotification: async (notificationId: string) => {
    return apiCall(`/notifications/${notificationId}`, { method: 'DELETE' });
  },
};

export const reportAPI = {
  create: async (params: {
    entity_type: 'listing' | 'user' | 'event' | 'question';
    entity_id: string;
    reason: string;
    comment?: string;
  }) => {
    return apiCall('/reports', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

export const blockAPI = {
  // Returns the set of user IDs the current user has blocked.
  getBlockedIds: async (): Promise<Set<string>> => {
    try {
      const me = authAPI.getCurrentUserId();
      if (!me) return new Set();
      const supabase = getSupabaseClient(getAuthToken());
      const { data, error } = await supabase
        .from('blocked_users')
        .select('blocked_user_id')
        .eq('blocker_id', me);
      if (error || !data) return new Set();
      return new Set(data.map((r: any) => String(r.blocked_user_id)));
    } catch {
      return new Set();
    }
  },

  block: async (targetUserId: string) => {
    const me = authAPI.getCurrentUserId();
    if (!me) throw new Error('Not authenticated');
    if (me === targetUserId) throw new Error('Cannot block yourself');
    const supabase = getSupabaseClient(getAuthToken());
    const { error } = await supabase
      .from('blocked_users')
      .upsert(
        { blocker_id: me, blocked_user_id: targetUserId },
        { onConflict: 'blocker_id,blocked_user_id' }
      );
    if (error) throw error;
  },

  unblock: async (targetUserId: string) => {
    const me = authAPI.getCurrentUserId();
    if (!me) throw new Error('Not authenticated');
    const supabase = getSupabaseClient(getAuthToken());
    const { error } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocker_id', me)
      .eq('blocked_user_id', targetUserId);
    if (error) throw error;
  },

  isBlocked: async (targetUserId: string): Promise<boolean> => {
    const ids = await blockAPI.getBlockedIds();
    return ids.has(String(targetUserId));
  },
};
